import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  Search, Filter, ChevronDown, ChevronRight, X, Check,
  CheckCircle, XCircle, Clock, Loader2, AlertTriangle,
  Send, Mail, Smartphone, MessageSquare, Phone,
  TrendingUp, Package, Star, ShieldAlert, Target, Users,
  Building2, Eye, RotateCw, Calendar, RefreshCw, Store,
} from 'lucide-react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/Admin/Layout/AdminLayout';
import FooterBar from '../../components/shared/FooterBar';
import { notificationDeliveryService } from '../../services/notificationDeliveryService';
import { sellerService } from '../../services/sellerService';
import { useSeller } from '../../context/SellerContext';
import MarketplaceSelector from '../../components/shared/MarketplaceSelector';

// ─── Searchable Seller Dropdown ─────────────────────────────────────────────

function SellerSearchDropdown({ value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState({});
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const selectedOption = options.find(o => o.value === value);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target) &&
          dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!open) { setSearch(''); return; }
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < 240;
    setDropdownStyle({
      position: 'fixed',
      left: rect.left,
      width: Math.max(rect.width, 220),
      ...(openAbove ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      zIndex: 9999,
    });
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  const filtered = options.filter(o =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 px-2.5 py-[5px] text-[12px] border rounded bg-white transition-all min-w-[150px] ${
          open ? 'border-[#f08804] ring-2 ring-[#f08804]/20' : 'border-[#D5D9D9] hover:border-[#888]'
        }`}
      >
        <Store size={14} className="text-[#999] flex-shrink-0" />
        <span className={`flex-1 text-left truncate ${selectedOption ? 'text-[#0F1111]' : 'text-[#999]'}`}>
          {selectedOption ? selectedOption.label : 'All Sellers'}
        </span>
        <ChevronDown size={14} className={`text-[#999] transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && ReactDOM.createPortal(
        <div ref={dropdownRef} style={dropdownStyle} className="bg-white border border-[#D5D9D9] rounded-lg shadow-lg overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-[#E7E7E7]">
            <Search size={14} className="text-[#999] flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search sellers..."
              className="flex-1 bg-transparent outline-none text-[13px] text-[#0F1111] placeholder-[#999]"
            />
          </div>
          <div className="max-h-52 overflow-y-auto">
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-[13px] text-left transition-colors ${
                !value ? 'bg-[#FFF4E6] text-[#C45500] font-medium' : 'text-[#0F1111] hover:bg-[#F7F7F7]'
              }`}
            >
              <span className="flex-1">All Sellers</span>
              {!value && <Check size={14} className="text-[#C45500]" />}
            </button>
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-[13px] text-[#999] text-center">No sellers found</div>
            ) : filtered.map(o => (
              <button
                key={o.value}
                type="button"
                onClick={() => { onChange(o.value); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-[13px] text-left transition-colors ${
                  o.value === value ? 'bg-[#FFF4E6] text-[#C45500] font-medium' : 'text-[#0F1111] hover:bg-[#F7F7F7]'
                }`}
              >
                <span className="flex-1 truncate">{o.label}</span>
                {o.value === value && <Check size={14} className="text-[#C45500]" />}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Status Config ──────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  SENT: { label: 'Sent', icon: CheckCircle, color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  DELIVERED: { label: 'Delivered', icon: CheckCircle, color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  PARTIAL: { label: 'Partial', icon: AlertTriangle, color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-500' },
  FAILED: { label: 'Failed', icon: XCircle, color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500' },
  QUEUED: { label: 'Queued', icon: Clock, color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200', dot: 'bg-slate-400' },
  SENDING: { label: 'Sending', icon: Loader2, color: 'text-[#C45500]', bg: 'bg-[#FFF4E6]', border: 'border-[#FFD89E]', dot: 'bg-[#232F3E]' },
  BOUNCED: { label: 'Bounced', icon: AlertTriangle, color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-500' },
  GENERATED: { label: 'Generated', icon: Eye, color: 'text-[#C45500]', bg: 'bg-[#FFF4E6]', border: 'border-[#FFD89E]', dot: 'bg-[#FFF4E6]0' },
};

const CHANNEL_CONFIG = {
  EMAIL: { label: 'Email', icon: Mail, color: 'text-[#C45500]', bg: 'bg-[#FFF4E6]', border: 'border-[#FFD89E]' },
  WHATSAPP: { label: 'WhatsApp', icon: Smartphone, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  SMS: { label: 'SMS', icon: MessageSquare, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
  SYSTEM: { label: 'Dashboard', icon: Eye, color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200' },
};

const TYPE_CONFIG = {
  DAILY_ORDER_SALES: { label: 'Orders & Sales', icon: TrendingUp, color: 'text-emerald-600' },
  PRODUCT_LISTING_STATUS: { label: 'Product Listings', icon: Package, color: 'text-[#C45500]' },
  FEEDBACK_REVIEW: { label: 'Reviews & Feedback', icon: Star, color: 'text-amber-600' },
  POLICY_WARNING: { label: 'Policy Warning', icon: ShieldAlert, color: 'text-rose-600' },
  SPONSORED_AD_PERFORMANCE: { label: 'Ad Performance', icon: Target, color: 'text-orange-600' },
  PRODUCT_HIJACK: { label: 'Hijack Alert', icon: Users, color: 'text-violet-600' },
  REVIEW_REQUEST: { label: 'Review Request', icon: Star, color: 'text-[#C45500]' },
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true });
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ─── Stat Card ──────────────────────────────────────────────────────────────

function StatCard({ label, value, icon: Icon, color, bg, border }) {
  return (
    <div className={`flex items-center gap-3.5 px-5 py-4 rounded-xl border ${border} ${bg}`}>
      <div className={`w-9 h-9 rounded-lg ${bg} border ${border} flex items-center justify-center`}>
        <Icon size={16} className={color} />
      </div>
      <div>
        <p className="text-xl font-bold text-slate-800">{value}</p>
        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{label}</p>
      </div>
    </div>
  );
}

// ─── Delivery Row ───────────────────────────────────────────────────────────

function DeliveryRow({ delivery, isExpanded, onToggle }) {
  const sc = STATUS_CONFIG[delivery.status] || STATUS_CONFIG.QUEUED;
  const cc = CHANNEL_CONFIG[delivery.channelType] || CHANNEL_CONFIG.EMAIL;
  const tc = TYPE_CONFIG[delivery.notificationType] || TYPE_CONFIG.DAILY_ORDER_SALES;
  const StatusIcon = sc.icon;
  const ChannelIcon = cc.icon;
  const TypeIcon = tc.icon;

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-sm transition-shadow">
      {/* Row */}
      <div className="flex items-center gap-4 px-5 py-3.5 cursor-pointer" onClick={onToggle}>
        {/* Expand arrow */}
        <div className="flex-shrink-0 text-slate-300">
          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </div>

        {/* Type icon */}
        <div className="w-8 h-8 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center flex-shrink-0">
          <TypeIcon size={14} className={tc.color} />
        </div>

        {/* Subject + Seller */}
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold text-slate-800 truncate">{delivery.subject || '(No subject)'}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[11px] text-slate-400">{delivery.sellerName || 'Unknown Seller'}</span>
            <span className="text-slate-200">|</span>
            <span className="text-[11px] text-slate-400">{tc.label}</span>
          </div>
        </div>

        {/* Channel badge */}
        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold border ${cc.bg} ${cc.color} ${cc.border}`}>
          <ChannelIcon size={10} />
          {cc.label}
        </div>

        {/* Recipients */}
        <div className="w-[160px] flex-shrink-0">
          {delivery.totalRecipients > 1 ? (
            <>
              <span className="text-[11px] text-slate-600 font-semibold">{delivery.totalRecipients} recipients</span>
              <span className="text-[10px] text-slate-400 block">
                {delivery.sentCount > 0 && `${delivery.sentCount} sent`}
                {delivery.failedCount > 0 && ` · ${delivery.failedCount} failed`}
              </span>
            </>
          ) : (
            <span className="text-[11px] text-slate-500 font-medium truncate block">{delivery.recipient}</span>
          )}
        </div>

        {/* Status badge */}
        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold border ${sc.bg} ${sc.color} ${sc.border} w-[95px] justify-center`}>
          <StatusIcon size={10} className={delivery.status === 'SENDING' ? 'animate-spin' : ''} />
          {sc.label}
        </div>

        {/* Time */}
        <div className="w-[100px] flex-shrink-0 text-right">
          <p className="text-[11px] font-medium text-slate-500">{formatDateTime(delivery.triggeredAt)}</p>
          <p className="text-[10px] text-slate-300">{timeAgo(delivery.triggeredAt)}</p>
        </div>
      </div>

      {/* Expanded Detail */}
      {isExpanded && (
        <div className="border-t border-slate-100 bg-slate-50/50">
          <div className="flex gap-6 p-5">
            {/* Message Content */}
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Message Content</p>
              <div className="bg-white rounded-lg border border-slate-200 p-4 text-[12px] text-slate-700 leading-relaxed whitespace-pre-wrap font-mono max-h-[240px] overflow-y-auto">
                {delivery.messageContent || 'No content available'}
              </div>
            </div>

            {/* Meta Info */}
            <div className="w-[220px] flex-shrink-0 space-y-3">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Details</p>
              <MetaRow label="Status" value={sc.label} valueClass={sc.color} />
              <MetaRow label="Channel" value={cc.label} />
              <MetaRow label="Triggered" value={formatDateTime(delivery.triggeredAt)} />
              {delivery.deliveredAt && <MetaRow label="Delivered" value={formatDateTime(delivery.deliveredAt)} />}

              {/* Recipients list */}
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
                  Recipients {delivery.totalRecipients > 1 ? `(${delivery.totalRecipients})` : ''}
                </p>
                {delivery.recipients && Array.isArray(delivery.recipients) ? (
                  <div className="space-y-1">
                    {delivery.recipients.map((r, i) => (
                      <div key={i} className="flex items-center justify-between text-[11px]">
                        <span className="text-slate-600 truncate flex-1">{r.recipient}</span>
                        <span className={`ml-2 text-[10px] font-semibold ${
                          r.status === 'SENT' ? 'text-emerald-600' :
                          r.status === 'FAILED' ? 'text-red-600' :
                          'text-amber-600'
                        }`}>{r.status}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[12px] text-slate-700">{delivery.recipient}</p>
                )}
              </div>
              {delivery.errorMessage && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-2">
                  <p className="text-[10px] font-bold text-red-600 uppercase tracking-wider mb-1">Error</p>
                  <p className="text-[11px] text-red-700 leading-relaxed">{delivery.errorMessage}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MetaRow({ label, value, valueClass = 'text-slate-700' }) {
  return (
    <div>
      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{label}</p>
      <p className={`text-[12px] font-medium ${valueClass} mt-0.5`}>{value}</p>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═════════════════════════════════════════════════════════════════════════════

export default function DeliveryActivityPage() {
  const { selectedSeller: sellerCtx } = useSeller();
  const isHubMode = !sellerCtx; // No seller selected = hub mode (show all sellers)
  const sellerId = sellerCtx?.id || '';
  const sellerChannels = useMemo(() => (sellerCtx?.channels || []).filter(c => c.accountStatus === 'AUTHORIZED'), [sellerCtx]);
  const [selectedAccountId, setSelectedAccountId] = useState('');

  // Hub mode: seller dropdown
  const [sellers, setSellers] = useState([]);
  const [hubSellerId, setHubSellerId] = useState('');

  // Date filter
  const todayStr = new Date().toISOString().split('T')[0];
  const yesterdayStr = new Date(Date.now() - 86400000).toISOString().split('T')[0];
  const last7Str = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];
  const last30Str = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];

  const [datePreset, setDatePreset] = useState('today');
  // Applied dates — what the API uses
  const [dateFrom, setDateFrom] = useState(todayStr);
  const [dateTo, setDateTo] = useState(todayStr);
  // Draft dates — for custom picker (only applied on "Apply" click)
  const [customFrom, setCustomFrom] = useState(todayStr);
  const [customTo, setCustomTo] = useState(todayStr);

  const applyDatePreset = (preset) => {
    setDatePreset(preset);
    switch (preset) {
      case 'today': setDateFrom(todayStr); setDateTo(todayStr); break;
      case 'yesterday': setDateFrom(yesterdayStr); setDateTo(yesterdayStr); break;
      case 'last7': setDateFrom(last7Str); setDateTo(todayStr); break;
      case 'last30': setDateFrom(last30Str); setDateTo(todayStr); break;
      case 'all': setDateFrom(''); setDateTo(''); break;
      case 'custom': break; // don't change applied dates yet — wait for Apply
    }
  };

  const applyCustomDates = () => {
    setDateFrom(customFrom);
    setDateTo(customTo);
  };

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterChannel, setFilterChannel] = useState('');
  const [filterType, setFilterType] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const requestIdRef = useRef(0);

  // Load sellers list for hub mode
  useEffect(() => {
    if (isHubMode) {
      sellerService.getAll().then(res => {
        const data = res.data || res;
        setSellers(Array.isArray(data) ? data : []);
      }).catch(() => {});
    }
  }, [isHubMode]);

  const effectiveSellerId = isHubMode ? hubSellerId : sellerId;

  const loadData = useCallback(async (isRefresh = false) => {
    const myRequestId = ++requestIdRef.current;
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      const params = {};
      if (effectiveSellerId) params.sellerId = effectiveSellerId;
      if (filterType) params.notificationType = filterType;
      if (filterChannel) params.channel = filterChannel;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;

      const deliveriesRes = await notificationDeliveryService.getAll(params);

      // Ignore stale responses
      if (myRequestId !== requestIdRef.current) return;

      const deliveriesData = Array.isArray(deliveriesRes) ? deliveriesRes : (Array.isArray(deliveriesRes?.data) ? deliveriesRes.data : []);
      setDeliveries(deliveriesData);

      if (isRefresh) toast.success('Data refreshed');
    } catch (err) {
      if (myRequestId !== requestIdRef.current) return;
      console.error('Failed to load delivery data:', err);
      toast.error('Failed to load delivery data');
    } finally {
      if (myRequestId === requestIdRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [effectiveSellerId, filterType, filterChannel, dateFrom, dateTo]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Stats — not affected by row filters
  const stats = useMemo(() => {
    const total = deliveries.length;
    const sent = deliveries.filter(d => d.status === 'SENT' || d.status === 'DELIVERED').length;
    const failed = deliveries.filter(d => d.status === 'FAILED' || d.status === 'BOUNCED' || d.status === 'PARTIAL').length;
    const queued = deliveries.filter(d => d.status === 'QUEUED' || d.status === 'SENDING').length;
    const generated = deliveries.filter(d => d.status === 'GENERATED').length;

    // Per-channel message counts (sum of recipients, not rows)
    const getSent = (d) => typeof d.sentCount === 'number' ? d.sentCount : ((d.status === 'SENT' || d.status === 'DELIVERED') ? (d.totalRecipients || 1) : 0);
    const getFailed = (d) => typeof d.failedCount === 'number' ? d.failedCount : ((d.status === 'FAILED' || d.status === 'BOUNCED') ? (d.totalRecipients || 1) : 0);
    const getRecipients = (d) => d.totalRecipients || 1;

    const sumByChannel = (rows, fn) => rows.reduce((acc, d) => {
      if (d.channelType === 'EMAIL') acc.EMAIL += fn(d);
      else if (d.channelType === 'WHATSAPP') acc.WHATSAPP += fn(d);
      else if (d.channelType === 'SMS') acc.SMS += fn(d);
      return acc;
    }, { EMAIL: 0, WHATSAPP: 0, SMS: 0 });

    const totalBreakdown = sumByChannel(deliveries, getRecipients);
    const sentBreakdown = sumByChannel(deliveries, getSent);
    const failedBreakdown = sumByChannel(deliveries, getFailed);
    const queuedBreakdown = sumByChannel(
      deliveries.filter(d => d.status === 'QUEUED' || d.status === 'SENDING'),
      getRecipients,
    );

    return { total, sent, failed, queued, generated, totalBreakdown, sentBreakdown, failedBreakdown, queuedBreakdown };
  }, [deliveries]);

  // Client-side filtering (status + search)
  const filtered = useMemo(() => {
    let result = deliveries;

    if (filterStatus) {
      if (filterStatus === 'SENT') {
        result = result.filter(d => d.status === 'SENT' || d.status === 'DELIVERED');
      } else if (filterStatus === 'FAILED') {
        result = result.filter(d => d.status === 'FAILED' || d.status === 'BOUNCED' || d.status === 'PARTIAL');
      } else {
        result = result.filter(d => d.status === filterStatus);
      }
    }

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(d =>
        d.sellerName?.toLowerCase().includes(q) ||
        d.subject?.toLowerCase().includes(q)
      );
    }

    return result;
  }, [deliveries, filterStatus, search]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;
  const totalItems = filtered.length;
  const totalPages = totalItems === 0 ? 1 : Math.ceil(totalItems / PAGE_SIZE);
  const paginatedDeliveries = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, currentPage]);

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [filterStatus, filterChannel, filterType, search, datePreset, hubSellerId]);

  const hasActiveFilters = filterStatus || filterChannel || filterType || datePreset !== 'today' || (isHubMode && hubSellerId);

  const clearFilters = () => { setFilterStatus(''); setFilterChannel(''); setFilterType(''); applyDatePreset('today'); if (isHubMode) setHubSellerId(''); };

  const selectCls = "px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-[11px] font-semibold focus:outline-none focus:ring-2 focus:ring-[#f08804] focus:border-transparent transition-colors appearance-none";

  if (loading) {
    return (
      <AdminLayout>
        <div className="h-full flex items-center justify-center">
          <div className="text-center">
            <Loader2 size={32} className="animate-spin text-[#f08804] mx-auto mb-3" />
            <p className="text-sm text-slate-400">Loading delivery activity...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const handleStatClick = (status) => setFilterStatus(filterStatus === status ? '' : status);

  return (
    <AdminLayout>
      <div className="min-h-full flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-[#D5D9D9] px-4 sm:px-6 py-2 flex items-center justify-between sticky top-0 z-10 gap-2">
          <h1 className="text-[14px] font-bold text-[#0F1111] flex-shrink-0">
            Delivery Activity {deliveries.length > 0 && <span className="font-normal text-[#565959]">({deliveries.length})</span>}
          </h1>
          <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto">
            {isHubMode && (
              <SellerSearchDropdown
                value={hubSellerId}
                onChange={setHubSellerId}
                options={sellers.map(s => ({ value: s.id, label: s.businessName }))}
              />
            )}
            <MarketplaceSelector value={selectedAccountId} onChange={setSelectedAccountId} sellerId={isHubMode ? hubSellerId : undefined} />
            <button
              onClick={() => loadData(true)}
              disabled={refreshing}
              className="p-1.5 rounded bg-white border border-[#D5D9D9] text-[#565959] hover:bg-[#F7F7F7] transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-3 sm:p-5 space-y-4">

        {/* Stats — clickable badges */}
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-3">
          {[
            { label: 'Total', value: stats.total, key: '', color: 'bg-[#232F3E]', breakdown: stats.totalBreakdown },
            { label: 'Sent', value: stats.sent, key: 'SENT', color: 'bg-emerald-500', breakdown: stats.sentBreakdown },
            { label: 'Failed', value: stats.failed, key: 'FAILED', color: 'bg-red-500', breakdown: stats.failedBreakdown },
            { label: 'Queued', value: stats.queued, key: 'QUEUED', color: 'bg-amber-500', breakdown: stats.queuedBreakdown },
            { label: 'System', value: stats.generated, key: 'GENERATED', color: 'bg-slate-400', breakdown: null },
          ].map(s => (
            <button key={s.label} onClick={() => handleStatClick(s.key)}
              className={`bg-white rounded-lg border p-4 text-left transition-all ${filterStatus === s.key ? 'border-[#f08804] ring-2 ring-[#f08804]/20' : 'border-[#D5D9D9] hover:border-[#f08804]/40'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{s.label}</p>
                  <p className="text-2xl font-bold text-gray-800 mt-1">{s.value}</p>
                </div>
                <div className={`${s.color} w-3 h-3 rounded-full`} />
              </div>
              {s.breakdown && (
                <div className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-2.5 text-[10px] font-semibold">
                  <span className={`inline-flex items-center gap-1 ${s.breakdown.EMAIL > 0 ? 'text-[#C45500]' : 'text-slate-300'}`} title="Email">
                    <Mail size={11} /> {s.breakdown.EMAIL}
                  </span>
                  <span className={`inline-flex items-center gap-1 ${s.breakdown.WHATSAPP > 0 ? 'text-emerald-600' : 'text-slate-300'}`} title="WhatsApp">
                    <Smartphone size={11} /> {s.breakdown.WHATSAPP}
                  </span>
                  <span className={`inline-flex items-center gap-1 ${s.breakdown.SMS > 0 ? 'text-amber-600' : 'text-slate-300'}`} title="SMS">
                    <MessageSquare size={11} /> {s.breakdown.SMS}
                  </span>
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Filter Bar */}
        <div className="bg-white rounded-lg border border-[#D5D9D9] p-3 sm:p-4">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <div className="relative flex-1 max-w-xs">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by seller, subject..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]" />
              {search && <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"><X size={14} /></button>}
            </div>

            <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]">
              <option value="">All Types</option>
              {Object.entries(TYPE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>

            <select value={filterChannel} onChange={(e) => setFilterChannel(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]">
              <option value="">All Channels</option>
              {Object.entries(CHANNEL_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>

            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]">
              <option value="">All Statuses</option>
              <option value="SENT">Sent</option>
              <option value="FAILED">Failed</option>
              <option value="PARTIAL">Partial</option>
              <option value="QUEUED">Queued</option>
              <option value="GENERATED">System Only</option>
            </select>

            {/* Date presets */}
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
              {[
                { key: 'today', label: 'Today' },
                { key: 'yesterday', label: 'Yesterday' },
                { key: 'last7', label: '7 Days' },
                { key: 'last30', label: '30 Days' },
                { key: 'all', label: 'All' },
                { key: 'custom', label: 'Custom' },
              ].map(p => (
                <button
                  key={p.key}
                  onClick={() => applyDatePreset(p.key)}
                  className={`px-2.5 py-1.5 text-[11px] font-semibold rounded-md transition-all ${
                    datePreset === p.key
                      ? 'bg-white text-slate-800 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {datePreset === 'custom' && (
              <div className="flex items-center gap-1.5">
                <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                  className="px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-[12px] focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]" />
                <span className="text-[11px] text-slate-400">to</span>
                <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                  className="px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-[12px] focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]" />
                <button onClick={applyCustomDates}
                  className="px-3 py-1.5 text-[11px] font-semibold text-white bg-[#232F3E] hover:bg-[#37475A] rounded-lg transition-colors shadow-sm">
                  Apply
                </button>
              </div>
            )}

            {hasActiveFilters && (
              <button onClick={clearFilters} className="inline-flex items-center px-3 py-2 text-sm text-red-600 hover:text-red-800">
                <X size={14} className="mr-1" /> Clear
              </button>
            )}

            <span className="ml-auto text-sm text-gray-500">
              {filtered.length} of {deliveries.length} records
            </span>
          </div>
        </div>

        {/* List */}
        <div className="space-y-2.5">
            {paginatedDeliveries.length === 0 ? (
              <div className="bg-white rounded-lg border border-[#D5D9D9] py-16 text-center">
                <Send size={40} className="mx-auto text-[#D5D9D9] mb-3" />
                <p className="text-[#0F1111] text-sm font-semibold">No deliveries found</p>
                <p className="text-[#565959] text-[13px] mt-1">
                  {hasActiveFilters || search ? 'Try adjusting your filters.' : 'Notification deliveries will appear here once triggered.'}
                </p>
                {(hasActiveFilters || search) && (
                  <button onClick={() => { clearFilters(); setSearch(''); }} className="mt-3 text-[12px] font-semibold text-[#C45500]">Clear all filters</button>
                )}
              </div>
            ) : (
              paginatedDeliveries.map(d => (
                <DeliveryRow key={d.id} delivery={d} isExpanded={expandedId === d.id} onToggle={() => setExpandedId(expandedId === d.id ? null : d.id)} />
              ))
            )}
        </div>
        </div>

        {/* Footer */}
        <FooterBar
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          totalItems={totalItems}
          pageSize={PAGE_SIZE}
        />
      </div>
    </AdminLayout>

  );
}
